/**
 * Save Strategy Implementations
 * Handle three save targets: parent, api, none
 */

import type { ISaveStrategy } from '../types/index';

/**
 * Parent Save Strategy
 * Delegate save to parent container
 */
export class ParentSaveStrategy implements ISaveStrategy {
  private onSave: ((formID: string, data: any) => Promise<void>) | null = null;

  constructor(onSave?: (formID: string, data: any) => Promise<void>) {
    this.onSave = onSave || null;
  }

  async canSave(): Promise<boolean> {
    return this.onSave !== null;
  }

  async save(formID: string, data: any): Promise<void> {
    if (!this.onSave) {
      throw new Error('Parent save handler not configured');
    }
    await this.onSave(formID, data);
  }

  onSaveError(error: Error): void {
    console.error('Parent save failed:', error);
  }
}

/**
 * API Save Strategy
 * POST data to API endpoint
 * Includes retry logic with exponential backoff
 */
export class ApiSaveStrategy implements ISaveStrategy {
  private apiUrl: string;
  private maxRetries = 3;
  private baseDelay = 1000; // 1 second

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  async canSave(): Promise<boolean> {
    return !!this.apiUrl;
  }

  async save(formID: string, data: any): Promise<void> {
    try {
      await this.saveWithRetry(formID, data);
    } catch (error) {
      this.onSaveError(error as Error);
      throw error;
    }
  }

  private async saveWithRetry(
    formID: string,
    data: any,
    attempt = 0
  ): Promise<void> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formID, data }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
    } catch (error) {
      if (attempt < this.maxRetries) {
        const delay = this.baseDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.saveWithRetry(formID, data, attempt + 1);
      }
      throw error;
    }
  }

  onSaveError(error: Error): void {
    console.error(`API save to ${this.apiUrl} failed:`, error);
  }
}

/**
 * No Save Strategy
 * Do nothing on save (useful for read-only forms)
 */
export class NoSaveStrategy implements ISaveStrategy {
  async canSave(): Promise<boolean> {
    return true;
  }

  async save(): Promise<void> {
    // No-op: don't save anything
    console.log('No save strategy: skipping save');
  }

  onSaveError(error: Error): void {
    console.warn('No save strategy, error ignored:', error);
  }
}

/**
 * Save Strategy Factory
 */
export class SaveStrategyFactory {
  static createStrategy(
    target: 'parent' | 'api' | 'none',
    config?: { apiUrl?: string; onSave?: (formID: string, data: any) => Promise<void> }
  ): ISaveStrategy {
    switch (target) {
      case 'parent':
        return new ParentSaveStrategy(config?.onSave);
      case 'api':
        if (!config?.apiUrl) {
          throw new Error('API URL required for api save strategy');
        }
        return new ApiSaveStrategy(config.apiUrl);
      case 'none':
        return new NoSaveStrategy();
      default:
        throw new Error(`Unknown save target: ${target}`);
    }
  }
}
